/**
 * Utility functions for Shaka Player version comparison and compatibility checks
 */

/**
 * Compares two version strings.
 * @param version1 - First version string
 * @param version2 - Second version string
 * @returns Negative if version1 < version2, 0 if equal, positive if version1 > version2
 */
export function compareVersions(version1: string, version2: string): number {
  // Extract core version numbers by removing any suffix after hyphen or plus sign
  // Also remove 'v' prefix if present (e.g., v4.14.28 -> 4.14.28)
  // This handles cases like: v4.14.28, 4.13.0-dirty, 4.13.0-beta.1, 4.13.0+build.123, etc.
  const cleanVersion1 = version1.replace(/^v/, '').split(/[-+]/)[0];
  const cleanVersion2 = version2.replace(/^v/, '').split(/[-+]/)[0];

  const parts1 = cleanVersion1.split('.').map(Number);
  const parts2 = cleanVersion2.split('.').map(Number);

  const maxLength = Math.max(parts1.length, parts2.length);

  for (let i = 0; i < maxLength; i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;

    if (part1 < part2) return -1;
    if (part1 > part2) return 1;
  }

  return 0;
}

/**
 * Checks if the current Shaka Player version meets the minimum version requirement
 * @param currentVersion - Current Shaka Player version
 * @param minVersion - Minimum required version
 * @returns True if current version meets or exceeds minimum version
 */
export function isVersionSupported(currentVersion: string, minVersion: string): boolean {
  return compareVersions(currentVersion, minVersion) >= 0;
}
